import React, { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { MobileSignRequestStyles } from "./MobileSignRequest.styles";
import MobileFlowHeader, {
  MobileHeaderMenuItem,
} from "./components/MobileFlowHeader";
import MobileBottomBar from "./components/MobileBottomBar";
import { MobileStep } from "./components/MobileStepper";
import MobileUploadDocument from "./steps/MobileUploadDocument";
import MobileApproversAndSigners, {
  addMobileApprover,
  addMobileGroup,
  addMobileSigner,
} from "./steps/MobileApproversAndSigners";
import MobileSignRequestEditor from "./steps/MobileSignRequestEditor";
import MobileMode from "./steps/MobileMode";
import urls from "global/constants/UrlConstants";
import MobileSend from "./steps/MobileSend";
import MobileConfig from "./steps/MobileConfig";
import MobilePreview from "./steps/MobilePreview";

/**
 * Visual step pills for the mobile flow. NOTE: the mapping between these 6 pills
 * and the existing 4 desktop steps is being defined screen-by-screen with the
 * designer; for now only step 1 (Upload) is implemented.
 */
const MOBILE_STEP_LABELS = [
  "Upload",
  "Signers",
  "Fields",
  "Mode",
  "Config",
  "Send",
] as const;

interface MobileSignRequestProps {
  flowProps: {
    useTemplate?: boolean;
    newTemplate?: boolean;
    updateTemplate?: boolean;
    editDraft?: boolean;
    copyRequest?: boolean;
    updateRequest?: boolean;
  };
  /** Desktop step descriptors (label/isActive/isCompleted) from the container. */
  steps: { label: string; isActive: boolean; isCompleted: boolean }[];
  currentStep: number;
  changeStep: (nextStep: number) => void;
  handleNext: (updateRequest?: boolean) => void;
  handlePrevious: () => void;
  onBack: () => void;
  menuItems: MobileHeaderMenuItem[];
  continueLabel: string;
  isContinueDisabled: boolean;
  /** SMS wallet, for the credit callout on the Signers step. */
  smsCreditBalance?: number;
  smsCreditCost?: number;
  smsCreditMaxCost?: number;
  showSmsCreditBanner?: boolean;
  /**
   * Self-sign flow renders a reduced 3-pill flow: Upload → Mode → Fields
   * (no Signers / Reminders / Send). `steps` from the container are just
   * ["Upload Document", "Sign Request Editor"]; "Mode" is inserted as a
   * mobile-only screen between them.
   */
  selfSign?: boolean;
  /** Plan quota / gating for the self-sign "Mode" (eSign type) screen. */
  selfSignMode?: {
    basicRemaining: number;
    certifiedRemaining: number;
    certifiedDisabled: boolean;
  };
}

const MobileSignRequest: React.FC<MobileSignRequestProps> = ({
  flowProps,
  steps,
  currentStep,
  changeStep,
  handleNext,
  onBack,
  menuItems,
  continueLabel,
  isContinueDisabled,
  smsCreditBalance,
  smsCreditCost,
  smsCreditMaxCost,
  showSmsCreditBanner,
  selfSign,
  selfSignMode,
}) => {
  const theme = useTheme();
  const classes = MobileSignRequestStyles(theme);
  const dispatch = useAppDispatch();
  const signRequest = useAppSelector((state: RootState) => state.app.signRequest);
  const [signerTab, setSignerTab] = useState<"signer" | "approver">("signer");
  const [approverMenuAnchor, setApproverMenuAnchor] =
    useState<null | HTMLElement>(null);
  // Mobile-only sub-step: the mobile flow inserts "Mode" (after the editor) and
  // "Send" (after the config) which don't exist as desktop steps. sub=1 means
  // "show the mobile-only screen that follows the current desktop step".
  const [mobileSub, setMobileSub] = useState<0 | 1>(0);
  // The Send step opens an email "Preview" overlay before the final send.
  const [previewOpen, setPreviewOpen] = useState(false);

  /*
    Choosing between Basic and Certified eSign is a non-prod affordance: the
    desktop self-sign flow renders its "Select eSign Mode" row behind
    `!urls.PROD`, and the generic flow's eSign section is commented out
    entirely. Mobile follows suit — on prod the "Mode" screen and its pill are
    dropped from both flows, leaving every request Certified (the redux
    default), and on uat/dev the choice stays exactly as it is today.
  */
  const showModeStep = !urls.PROD;

  // ---------------------------------------------------------------------------
  // Self-sign flow: a reduced 3-step flow (Upload → Mode → Fields). The desktop
  // `steps` are just ["Upload Document", "Sign Request Editor"]; "Mode" (the
  // eSign-type choice) is a mobile-only screen tucked between them, keyed off the
  // Upload step + `mobileSub`.
  // ---------------------------------------------------------------------------
  if (selfSign) {
    const pills = showModeStep
      ? ["Upload", "Mode", "Fields"]
      : ["Upload", "Fields"];
    const uploadStepIndex = steps.findIndex((s) => s.label === "Upload Document");
    const fieldsStepIndex = steps.findIndex(
      (s) => s.label === "Sign Request Editor",
    );

    const ssScreen =
      currentStep === fieldsStepIndex
        ? "Fields"
        : showModeStep && mobileSub === 1
          ? "Mode"
          : "Upload";
    const ssActive = pills.indexOf(ssScreen);
    const ssStepper: MobileStep[] = pills.map((label, index) => ({
      label,
      isActive: index === ssActive,
      isCompleted: index < ssActive,
    }));
    const ssIsEditor = ssScreen === "Fields";
    const ssTitle =
      ssScreen === "Mode"
        ? "eSign mode"
        : ssScreen === "Fields"
          ? "Place fields"
          : "Upload Document";

    const ssRenderStep = () => {
      if (ssScreen === "Mode")
        return (
          <MobileMode
            selfSign
            basicRemaining={selfSignMode?.basicRemaining ?? 0}
            certifiedRemaining={selfSignMode?.certifiedRemaining ?? 0}
            certifiedDisabled={selfSignMode?.certifiedDisabled ?? false}
          />
        );
      if (ssScreen === "Fields") return <MobileSignRequestEditor />;
      return (
        <MobileUploadDocument
          updateTemplate={!!flowProps.updateTemplate}
          isNewTemplate={!!flowProps.newTemplate}
        />
      );
    };

    const ssGoNext = () => {
      if (ssScreen === "Upload") {
        if (showModeStep) {
          setMobileSub(1); // Upload → Mode
        } else {
          handleNext(flowProps.updateRequest); // Upload → Fields (prod)
        }
      } else if (ssScreen === "Mode") {
        setMobileSub(0);
        handleNext(flowProps.updateRequest); // Mode → Fields (validate upload)
      } else {
        handleNext(flowProps.updateRequest); // Fields → send self-sign request
      }
    };

    const ssPillClick = (pillIndex: number) => {
      const pill = pills[pillIndex];
      if (pill === "Fields" && fieldsStepIndex >= 0) {
        changeStep(fieldsStepIndex);
        setMobileSub(0);
      } else if (pill === "Mode") {
        if (uploadStepIndex >= 0) changeStep(uploadStepIndex);
        setMobileSub(1);
      } else {
        if (uploadStepIndex >= 0) changeStep(uploadStepIndex);
        setMobileSub(0);
      }
    };

    return (
      <Box
        sx={{
          ...classes.root,
          ...(ssIsEditor && { height: "100dvh", overflow: "hidden" }),
        }}
      >
        <MobileFlowHeader
          title={ssTitle}
          steps={ssStepper}
          onBack={onBack}
          onStepClick={ssPillClick}
          menuItems={menuItems}
        />

        <Box
          sx={{
            ...classes.body,
            pt: ssScreen === "Upload" ? 2 : 0,
            ...(ssIsEditor && {
              px: 0,
              pb: "78px",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }),
          }}
        >
          {ssRenderStep()}
        </Box>

        <MobileBottomBar
          rightLabel={ssScreen === "Fields" ? continueLabel : "Continue"}
          onRightClick={ssGoNext}
          rightDisabled={
            isContinueDisabled ||
            !signRequest.files ||
            signRequest.files.length === 0
          }
        />
      </Box>
    );
  }

  const currentLabel = steps[currentStep]?.label ?? "";

  // The "I am the only signer" flow (type=signer) has no Signers step, so drop
  // the "Signers" pill; the desktop `steps` array already reflects this.
  const hasSignersStep = steps.some(
    (s) => s.label === "Approvers and Signers" || s.label === "Signers",
  );
  const mobilePills: string[] = MOBILE_STEP_LABELS.filter(
    (label) =>
      (hasSignersStep || label !== "Signers") &&
      (showModeStep || label !== "Mode"),
  );

  // Desktop steps that own a mobile-only follow-up screen.
  const editorStepIndex = steps.findIndex(
    (s) => s.label === "Sign Request Editor",
  );
  const configStepIndex = steps.findIndex(
    (s) => s.label === "Email configurations",
  );

  const labelToPill: Record<string, string> = {
    "Upload Document": "Upload",
    "Approvers and Signers": "Signers",
    Signers: "Signers",
    "Sign Request Editor": "Fields",
    "Email configurations": "Config",
  };

  // Resolve the current mobile screen from the desktop step + the sub-step.
  const screen =
    showModeStep && mobileSub === 1 && currentStep === editorStepIndex
      ? "Mode"
      : mobileSub === 1 && currentStep === configStepIndex
        ? "Send"
        : (labelToPill[currentLabel] ?? currentLabel);

  const activePillIndex = mobilePills.indexOf(screen);

  // Figma labels the 5th pill "Reminders"; the internal step logic keys off
  // "Config", so only the displayed label is remapped here.
  const PILL_DISPLAY_LABEL: Record<string, string> = { Config: "Reminders" };
  const stepperItems: MobileStep[] = mobilePills.map((label, index) => ({
    label: PILL_DISPLAY_LABEL[label] ?? label,
    isActive: index === activePillIndex,
    isCompleted: index < activePillIndex,
  }));

  const TITLE_BY_LABEL: Record<string, string> = {
    "Upload Document": "Upload Document",
    "Approvers and Signers": "Signers",
    Signers: "Signers",
    "Sign Request Editor": "Place fields",
  };
  const headerTitle =
    screen === "Mode"
      ? "Signing mode"
      : screen === "Send"
        ? previewOpen
          ? "Preview of document"
          : "Send"
        : screen === "Config"
          ? "Preferences"
          : (TITLE_BY_LABEL[currentLabel] ?? currentLabel);

  const renderStep = () => {
    if (screen === "Mode") return <MobileMode />;
    if (screen === "Config") return <MobileConfig />;
    if (screen === "Send") return previewOpen ? <MobilePreview /> : <MobileSend />;
    switch (currentLabel) {
      case "Upload Document":
        return (
          <MobileUploadDocument
            updateTemplate={!!flowProps.updateTemplate}
            isNewTemplate={!!flowProps.newTemplate}
          />
        );
      case "Approvers and Signers":
        return (
          <MobileApproversAndSigners
            tabValue={signerTab}
            setTabValue={setSignerTab}
            smsCreditBalance={smsCreditBalance}
            smsCreditCost={smsCreditCost}
            smsCreditMaxCost={smsCreditMaxCost}
            showSmsCreditBanner={showSmsCreditBanner}
          />
        );
      case "Sign Request Editor":
        return <MobileSignRequestEditor />;
      default:
        return (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography
              sx={{ color: theme.palette.mobile.grey[400], fontWeight: 500 }}
            >
              This step is coming soon.
            </Typography>
          </Box>
        );
    }
  };

  const isUploadStep = screen === "Upload";
  const isSignersStep = screen === "Signers";
  const isEditorStep = screen === "Fields";
  const isApproverTab = isSignersStep && signerTab === "approver";
  // Every mobile step advances via a "Continue" button. On the Send step,
  // "Continue" opens the email preview; the preview's "Confirm & Send" sends.
  const mobileContinueLabel = "Continue";

  // Advance through the mobile flow, inserting Mode (after the editor) and
  // Send (after the config) around the desktop steps.
  const goNext = () => {
    if (currentStep === editorStepIndex && mobileSub === 0 && showModeStep) {
      setMobileSub(1); // Fields → Mode
    } else if (currentStep === editorStepIndex && mobileSub === 1) {
      setMobileSub(0);
      handleNext(flowProps.updateRequest); // Mode → Config (validate editor)
    } else if (currentStep === configStepIndex && mobileSub === 0) {
      setMobileSub(1); // Config → Send
    } else if (currentStep === configStepIndex && mobileSub === 1) {
      handleNext(flowProps.updateRequest); // Send → actually send
    } else {
      handleNext(flowProps.updateRequest); // Upload / Signers → next desktop step
    }
  };

  // Stepper pill navigation, mapping a pill back to its desktop step / sub-step.
  const handlePillClick = (pillIndex: number) => {
    setPreviewOpen(false);
    const pill = mobilePills[pillIndex];
    if (pill === "Mode" && editorStepIndex >= 0) {
      changeStep(editorStepIndex);
      setMobileSub(1);
      return;
    }
    if (pill === "Send" && configStepIndex >= 0) {
      changeStep(configStepIndex);
      setMobileSub(1);
      return;
    }
    const pillToLabel: Record<string, string> = {
      Upload: "Upload Document",
      Signers: "Approvers and Signers",
      Fields: "Sign Request Editor",
      Config: "Email configurations",
    };
    const targetLabel = pillToLabel[pill];
    const targetStep = steps.findIndex(
      (s) =>
        s.label === targetLabel ||
        (targetLabel === "Approvers and Signers" && s.label === "Signers"),
    );
    if (targetStep >= 0) {
      changeStep(targetStep);
      setMobileSub(0);
    }
  };

  // Bottom-bar left action: "Add …" on the Signers step, "Save as template" on
  // the Send step.
  const isSendScreen = screen === "Send";
  const saveTemplateItem = menuItems.find(
    (i) => i.label === "Save as Template",
  );
  const showSaveTemplate = isSendScreen && !!saveTemplateItem;

  const leftLabel = isSignersStep
    ? isApproverTab
      ? "Add Approver/Group"
      : "Add Signers"
    : showSaveTemplate
      ? "Save as template"
      : undefined;

  const handleLeftClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isSignersStep) {
      if (isApproverTab) {
        setApproverMenuAnchor(e.currentTarget); // choose Approver or Group
      } else {
        addMobileSigner(signRequest.signers ?? [], dispatch);
      }
    } else if (showSaveTemplate) {
      saveTemplateItem!.onClick();
    }
  };

  // Send step: "Continue" opens the preview; on the preview, "Confirm & Send"
  // performs the actual send. Every other step just advances.
  const isPreview = previewOpen && isSendScreen;
  const rightLabel = isPreview
    ? hasSignersStep
      ? "Confirm & Send"
      : "Confirm & Sign"
    : mobileContinueLabel;

  const handleRightClick = () => {
    if (isPreview) {
      goNext();
    } else if (isSendScreen) {
      setPreviewOpen(true);
    } else {
      goNext();
    }
  };

  const handleBack = () => {
    if (isPreview) {
      setPreviewOpen(false);
      return;
    }
    onBack();
  };

  return (
    <Box
      sx={{
        ...classes.root,
        // Editor step: lock to the viewport so ONLY the PDF scrolls
        // (header, placing-for, toolbar and Continue stay fixed).
        ...(isEditorStep && { height: "100dvh", overflow: "hidden" }),
      }}
    >
      <MobileFlowHeader
        title={headerTitle}
        steps={stepperItems}
        onBack={handleBack}
        onStepClick={handlePillClick}
        menuItems={menuItems}
      />

      <Box
        sx={{
          ...classes.body,
          pt: isUploadStep || isSendScreen ? 2 : 0,
          // Editor fills the viewport; reserve the fixed Continue bar (~66px)
          // + a 12px gap so the field toolbar sits above it like Figma.
          ...(isEditorStep && {
            px: 0,
            pb: "78px",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }),
        }}
      >
        {renderStep()}
      </Box>

      <MobileBottomBar
        leftLabel={leftLabel}
        leftIcon={
          isSignersStep && !isApproverTab ? (
            <AddIcon
              sx={{ fontSize: 20, color: theme.palette.mobile.base[500] }}
            />
          ) : undefined
        }
        leftTrailingIcon={
          isApproverTab ? (
            <KeyboardArrowDownIcon
              sx={{ fontSize: 20, color: theme.palette.mobile.base[500] }}
            />
          ) : undefined
        }
        onLeftClick={handleLeftClick}
        rightLabel={rightLabel}
        onRightClick={handleRightClick}
        rightDisabled={
          isContinueDisabled ||
          !signRequest.files ||
          signRequest.files.length === 0
        }
      />

      <Menu
        anchorEl={approverMenuAnchor}
        open={Boolean(approverMenuAnchor)}
        onClose={() => setApproverMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        MenuListProps={{ "aria-label": "Add approver or group" }}
      >
        <MenuItem
          onClick={() => {
            addMobileApprover(signRequest.approval?.reviewers ?? [], dispatch);
            setApproverMenuAnchor(null);
          }}
        >
          <Typography variant="subtitle2">Add Approver</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            addMobileGroup(signRequest.approval?.reviewers ?? [], dispatch);
            setApproverMenuAnchor(null);
          }}
        >
          <Typography variant="subtitle2">Add Group</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MobileSignRequest;
